/* PUSHER */
import Pusher, { Channel } from 'pusher-js';
/* TYPES */
import type PusherType from 'pusher-js/types/src/core/pusher';

import ViktorClient from '@/api/clients/viktorClient/ViktorClient';
import { HttpStatus } from '@/enums/httpStatus';
/* STORES */
import { useGlobalStore } from '@/stores/global';

const PusherClient = (() => {
  /* Instance of client that includes all methods */
  let instance;

  /* Pusher instance */
  let pusher: PusherType;

  const constructor = function () {
    /**
     * Allows to get the current pusher instance
     */
    this.getInstance = function () {
      return pusher;
    };

    /**
     * Function called on init to configure the the pusher instance
     */
    this.init = function () {
      const globalStore = useGlobalStore();
      const pusherConf = new Pusher(globalStore.getPusher.key, {
        cluster: globalStore.getPusher.cluster,
        channelAuthorization: {
          endpoint: `${globalStore.getAPIURL}/broadcasting/auth`,
          transport: 'jsonp',
          customHandler: (params, cb) => {
            ViktorClient()
              .post(`/broadcasting/auth`, {
                socket_id: params.socketId,
                channel_name: params.channelName
              })
              .then(response => {
                cb(null, { auth: response.auth });
              })
              .catch(error => {
                if (error.status === HttpStatus.UNAUTHORIZED) {
                  pusher.disconnect();
                }
              });
          }
        }
      });
      pusher = pusherConf;
    };

    /**
     * Allows to subscribe to a given pusher channel if not already subscribe
     * @param {string} name - name of channel to subscribe to
     * @returns {Channel} - returns the created channel or the already existing one
     */
    this.subscribeToPusherChannel = function (name): Channel {
      const channelRequested = pusher.allChannels().find(channel => channel.name === name);
      if (!channelRequested) {
        return pusher.subscribe(name);
      } else {
        return channelRequested;
      }
    };

    /**
     * Allows to unsubscribe from a given channel if user is already subscribed
     * @param {string} name - name of channel to unsubscribe from
     * @returns {void}
     */
    this.unSubscribeFromPusherChannel = function (name): void {
      const channelRequested = pusher.allChannels().find(channel => channel.name === name);
      if (channelRequested) pusher.unsubscribe(name);
    };
  };

  /**
   * Create  instance
   */
  const createInstance = () => {
    instance = new constructor();
    instance.init();
    instance.constructor = null;
    return instance;
  };

  return () => {
    // Check if instance already exists
    return instance || createInstance();
  };
})();

export default PusherClient;
